/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import _ from 'lodash'
import React, { useContext, useEffect, useState } from 'react'
import { Button, Form, FormGroup } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import AckViewDocDialog from '~/components/AckViewDocDialog'
import { FsiPickerModal } from '~/components/FsiPicker'
import Modal from '~/components/Modal'
import { useModal } from '~/contexts/ModalContext'
import { useServiceContext } from '~/root'
import { getDetails, getFileReferences, getPdfUrlInShowFile, setFileReferences } from '~/services/api/files'
import { toast } from '~/services/toast'
import type { FsiItem } from '~/hooks/useFsiActionMenu'
import { useEsignature } from '~/hooks/useEsignature'

interface FsiReference {
  referenceFsiId: string | number
  referenceType: string
  filename: string
}

interface FsiReferencesModalProps {
  fsi: FsiItem
  onClose?: () => void
}

const FsiReferencesModal: React.FC<FsiReferencesModalProps> = ({ fsi, onClose }) => {
  const context = useServiceContext()
  const { t: translate } = useTranslation()
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [fsiReferencesOrig, setFsiReferencesOrig] = useState<FsiReference[]>([])
  const [fsiReferences, setFsiReferences] = useState<FsiReference[]>([])
  const [isFsiPickerOpen, setIsFsiPickerOpen] = useState<boolean>(false)
  const { showModal } = useModal()
  const { signAction } = useEsignature()
  const isUserEtmfSponsor: boolean = context.service.auth.getRole() === context.constant.user.roles.ETMF_SPONSOR

  useEffect(() => {
    setIsOpen(true)
    const loadReferences = () => {
      getFileReferences(typeof fsi.id === 'string' ? Number(fsi.id) : fsi.id).then(function (resp: any) {
        setFsiReferences(_.cloneDeep(resp))
        setFsiReferencesOrig(_.cloneDeep(resp))
      })
    }

    loadReferences()

    const updateFileReferencesListener = () => {
      loadReferences()
    }
    window.addEventListener('updateFile:references', updateFileReferencesListener)

    return () => {
      window.removeEventListener('updateFile:references', updateFileReferencesListener)
    }
  }, [fsi])

  const doClose = (): void => {
    setIsOpen(false)
    if (onClose) {
      onClose()
    }
  }

  const handleReset = (): void => {
    setFsiReferences([...fsiReferencesOrig])
  }

  const handleSubmit = (): void => {
    if (context.service.auth.requireESignatureForFileCommands()) {
      signAction('ADMIN_ACTION', 'ADMIN_ACTION_UPDATE_REFERENCES').then((validation) => {
        saveReferences(undefined, validation)
      })
      doClose()
    } else {
      saveReferences(undefined, undefined)
      doClose()
    }
  }

  const saveReferences = (pswModal: any, validation: any): Promise<any> => {
    const apiCall = setFileReferences(
      typeof fsi.id === 'string' ? Number(fsi.id) : fsi.id,
      fsiReferences.map((ref) => ({
        ...ref,
        referenceFsiId: typeof ref.referenceFsiId === 'string' ? Number(ref.referenceFsiId) : ref.referenceFsiId,
        referenceType: ref.referenceType as 'DOCUMENT' | 'FORM' | 'AMENDMENT'
      })),
      validation
    )

    apiCall.then(
      function () {
        setFsiReferencesOrig([...fsiReferences])
      },
      function (data: any) {
        toast.error(translate('ERROR_ERROR', { error: data.data.message }))
      }
    )

    return apiCall
  }

  const removeItem = (index: number): void => {
    setFsiReferences((prevReferences) => {
      const newReferences = [...prevReferences]
      newReferences.splice(index, 1)
      return newReferences
    })
  }

  const moveItem = (from: number, to: number): void => {
    setFsiReferences((prevReferences) => {
      const newReferences = [...prevReferences]
      const [moved] = newReferences.splice(from, 1)
      newReferences.splice(to, 0, moved)
      return newReferences
    })
  }

  const openFsiPicker = (): void => {
    setIsFsiPickerOpen(true)
  }

  const selectFsiDialog = (
    <FsiPickerModal
      options={{
        selector: 'file',
        showRC: true,
        showSystem: false,
        selectedId: null,
        onlyFoldersAndDocuments: true,
        leafIds: null
      }}
      title={translate('PLS_SELECT_FILE')}
      isOpen={isFsiPickerOpen}
      onClose={() => {
        setIsFsiPickerOpen(false)
      }}
      onSelect={(fsi: any) => {
        setIsFsiPickerOpen(false)
        if (fsi && fsi.id) {
          setFsiReferences((prevReferences) => [
            ...prevReferences,
            {
              referenceFsiId: fsi.id,
              referenceType: context.constant.file.referenceType.DOCUMENT,
              filename: fsi.fileName
            }
          ])
        }
      }}
    />
  )

  return (
    <>
      <Modal
        className="theme-sandman"
        title={translate('BROWSER_ACTION_REFERENCES')}
        show={isOpen}
        handleClose={doClose}
      >
        <Modal.Body>
          <FormGroup className="wparag">
            <Form.Label>{translate('DOCUMENT')}</Form.Label>
            <p className="whitespaceprewrap">{fsi.name}</p>
          </FormGroup>
          {fsiReferences.length ? (
            <>
              <table className="version-table w100p" style={{ border: 'none' }}>
                <thead>
                  <tr>
                    <th style={{ width: '800px' }}>{translate('NAME')}</th>
                    <th style={{ width: '300px' }}>{translate('TYPE')}</th>
                    {fsi?.actionFlags?.canManageReferences && <th>{translate('ACTIONS')}</th>}
                  </tr>
                </thead>
                <tbody>
                  {fsiReferences.map((item, index) => (
                    <tr key={index}>
                      <td className="clipped-cell">
                        <div className="ellipsis">
                          <a
                            href="#"
                            title={item?.filename}
                            onClick={(e) => {
                              e.preventDefault()
                              e.stopPropagation()
                              getDetails(
                                typeof item?.referenceFsiId === 'string'
                                  ? Number(item?.referenceFsiId)
                                  : item?.referenceFsiId
                              ).then((data) => {
                                showModal(
                                  <AckViewDocDialog
                                    pdfSrc={{
                                      service: () => {
                                        return getPdfUrlInShowFile(data?.id, -1, 0)
                                      }
                                    }}
                                    pdfInfo={{
                                      fsiId: data?.id
                                    }}
                                    thumbnailId={data?.thumbnailId}
                                    fileName={data?.name}
                                    isReferenceModal={true}
                                  />
                                )
                              })
                            }}
                          >
                            {item.filename}
                          </a>
                        </div>
                      </td>
                      <td>
                        <div>{translate('FSI_REFERENCE_TYPE_' + item?.referenceType)}</div>
                      </td>
                      {fsi?.actionFlags?.canManageReferences && (
                        <td>
                          <div className={`d-flex ${index === 0 ? 'align-right' : 'align-left'}`}>
                            {index !== 0 && (
                              <a
                                href="#"
                                className="d-block whitespacepre mr10"
                                onClick={(e) => {
                                  e.preventDefault()
                                  moveItem(index, --index)
                                }}
                              >
                                {translate('MOVE_UP')}
                              </a>
                            )}
                            {index !== fsiReferences.length - 1 && (
                              <a
                                href="#"
                                className="d-block whitespacepre"
                                onClick={(e) => {
                                  e.preventDefault()
                                  moveItem(index, ++index)
                                }}
                              >
                                {translate('MOVE_DOWN')}
                              </a>
                            )}
                            {fsiReferences.length === 1 && <span style={{ minWidth: '80px' }}>&nbsp;</span>}
                          </div>
                          {item.referenceType === context.constant.file.referenceType.DOCUMENT && (
                            <a
                              href="#"
                              className={`${isUserEtmfSponsor ? 'pointer-none' : ''} close`}
                              onClick={(e) => {
                                e.preventDefault()
                                removeItem(index)
                              }}
                            />
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          ) : (
            <FormGroup className="wparag mb0">
              <p className="whitespaceprewrap">{translate('BROWSER_REFERENCES_NO_REFERENCES_MSG')}</p>
            </FormGroup>
          )}
          {isFsiPickerOpen && selectFsiDialog}
          {fsi?.actionFlags?.canManageReferences && (
            <Button
              data-action={'add-new'}
              variant="default"
              onClick={openFsiPicker}
              className="btn btn-chita mt10 text-capitalize"
              disabled={isUserEtmfSponsor}
            >
              {translate('BROWSER_REFERENCES_BTN_ADD_NEW_REFERENCE')}
            </Button>
          )}
        </Modal.Body>
        <Modal.Footer>
          {fsi?.actionFlags?.canManageReferences && (
            <>
              {!_.isEqual(fsiReferences, fsiReferencesOrig) && (
                <Button
                  data-action={'cancel'}
                  onClick={handleReset}
                  variant="link"
                  disabled={_.isEqual(fsiReferences, fsiReferencesOrig)}
                  data-test-id="cancel"
                >
                  {translate('CANCEL')}
                </Button>
              )}
              <Button data-action={'save'} className="btn btn-primary" onClick={handleSubmit} data-test-id="save">
                {translate('OK')}
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>
    </>
  )
}
export default FsiReferencesModal
